import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Box, DialogActions, DialogContent, DialogTitle, Divider, Fade, IconButton, Tooltip, Typography} from "@mui/material";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {OpenInNew} from "@mui/icons-material";
import {Link} from "react-router-dom";
import {routes} from "../../../../core/models/static/routes/app-routes";
import classnames from "classnames";
import ObserversService from "../../../../core/services/observers";
import Banner from "../../app-specific/banner";
import LazyImage from "../../app-specific/lazy-image";
import LazySvg from "../../app-specific/lazy-svg";
import {Alert} from "@mui/lab";

const BannerSelectionDialog = ({
                                   open,
                                   close,
                                   existingBanners = [],
                                   multiSelect = false,
                                   excludeExistingBanners = false,
                                   industry,
                                   onSuccess,
                               }) => {
    const [_selectedBanners, setSelectedBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [_banners, setBanners] = useState([]);
    const isMounted = useIsMounted();

    const banners = useMemo(() => _banners.filter(e => {
        if (excludeExistingBanners) {
            if (existingBanners?.some(b => b.id === e.id))
                return false;
        }
        if (!industry?.id)
            return true;
        if (e.tags?.length === 0)
            return true;
        return !e.tags?.some(t => !!t.industryId && t.industryId !== industry?.id);
    }), [_banners, excludeExistingBanners, existingBanners, industry?.id]);

    const selectedBanners = useMemo(() =>
            Array.from(new Set([...(!excludeExistingBanners ? existingBanners ?? [] : []), ..._selectedBanners,].map(e => e.id)).values())
                .map(e => {
                    if (!excludeExistingBanners) {
                        const found = existingBanners?.find(b => b.id === e)
                        if (!!found)
                            return found
                    }
                    const found = _selectedBanners?.find(b => b.id === e)
                    if (!!found)
                        return found
                    return null;
                })
                .filter(e => !!e),
        [existingBanners, _selectedBanners, excludeExistingBanners])

    /**
     * Fetches the list of banners from the server.
     *
     * * if the response of the api is successful, sets the banners list state.
     * @return {Promise<void>}
     */
    const getBanners = useCallback(async () => {
        setLoading(true);
        const response = await Api.getBannersList();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setBanners(response?.data ?? []);
        }
        setLoading(false);
    }, [isMounted])

    /**
     * With each change in the open state of the dialog:
     * - fetches the list of banners from the server.
     */
    useEffect(() => {
        if (!open)
            return;
        void getBanners().then()
    }, [getBanners, open])


    useEffect(() => {
        if (!open)
            return;
        setSelectedBanners([])
    }, [open, excludeExistingBanners])

    /**
     * Toggles the selected state of the given banner.
     *
     * * if the banner exist in the list, removes it
     * * if the banner does not exist and multiselect prop is set, adds it
     * * if th banner does not exist and multiselect prop is not set, replaces the selected banner with this banner.
     * @param banner
     */
    const toggleSelectedBanner = useCallback((banner) => {
        setSelectedBanners(prevState => {
            if (!!prevState.find(e => e.id === banner.id)) {
                return prevState.filter(e => e.id !== banner.id);
            }
            if (!multiSelect) {
                return [banner]
            }
            return [...prevState, banner];
        })
    }, [multiSelect])

    /**
     * Commits the banner selection by invoking the onSuccess callback and then closes the dialog.
     */
    const commitSelection = useCallback(() => {
        onSuccess(selectedBanners);
        close();
    }, [close, onSuccess, selectedBanners])

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={(!banners?.length && !loading) ? 'sm' : 'lg'}
                loading={loading}
                disableEscapeKeyDown={loading}
                disableBackdropClick={loading}
                className={classnames('bound-dialog banner-selection-dialog', {'no-banners': (!banners?.length && !loading)})}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {
                            multiSelect
                                ? "Select Multiple Banners"
                                : "Select Banner"
                        } from {industry?.title ?? ""} Industry
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'dialog-body'}>
                    {
                        loading
                            ? Array(8).fill(null).map((_, e) => (
                                <Col key={e} xs={12} md={6} className={'loading extraLarge mb-3 px-2'}>
                                    <div/>
                                </Col>
                            ))
                            : !banners?.length
                                ? <Alert severity={'warning'} variant={'filled'} sx={{width: '100%', marginBlock: 4}} classes={{message: "flex-grow-1 p-0 overflow-hidden", icon: 'align-self-center'}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', marginBottom: 1, width: '100%', height: '100%'}}>
                                        <Typography variant={'body2'} sx={{flexGrow: 1}}>
                                            There are no banners to select from. You can manage your banners in the Banner Management page.
                                        </Typography>
                                        <IconButton
                                            size={'small'}
                                            color={'paper.primary'}
                                            component={Link}
                                            to={routes.userPlatform.bannerManagement}
                                            target={'_blank'}
                                            sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                            onClick={() => {
                                                // for some unknown reason, the Link does not work in this dialog. (something to do with
                                                // resize-observers notification error).
                                                window.open(window.location.origin + routes.userPlatform.bannerManagement, '_blank');
                                                close();
                                            }}
                                        >
                                            <OpenInNew/>
                                        </IconButton>
                                    </Box>
                                </Alert>
                                : banners?.map(banner => (
                                    <Col key={banner.id} xs={12} md={6} className={'mb-3 px-2'}>
                                        <BannerPreviewCard
                                            banner={banner}
                                            selected={!!selectedBanners.find(e => e.id === banner.id)}
                                            toggleSelected={toggleSelectedBanner}
                                        />
                                    </Col>
                                ))
                    }
                </DialogContent>
                <DialogActions>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={loading}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => close(false)}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Fade in={!!selectedBanners.length}>
                                <Button
                                    disabled={loading}
                                    variant={'dialog'}
                                    color={'primary'}
                                    onClick={commitSelection}
                                    borderRadius={'8px 0 8px 0'}
                                >
                                    {
                                        loading
                                            ? "Loading..."
                                            : `Select ${selectedBanners.length} ${selectedBanners.length === 1 ? 'banner' : 'banners'}`
                                    }
                                </Button>
                            </Fade>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    )
}


const BannerPreviewCard = ({banner, selected, toggleSelected}) => {
    const [tags, setTags] = useState([]);
    const [count, setCount] = useState(0);
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const tagContainerRef = useRef();
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const countRef = useRef();
    const containerDimensions = useRef({width: 0, height: 0});

    /**
     * With each change in the tags list of the banner:
     * - sets the tags state entries from the tags list of the banner.
     */
    useLayoutEffect(() => {
        setTags(banner?.tags?.map(e => ({
            ...e,
            iconErrored: false,
        })) ?? []);
        setCount(0);
    }, [banner?.tags])


    /**
     * With each change in the tagContainerRef object:
     * - attaches a new resize observer that would listen for changes in the tagContainerRef and update the
     * displayed tags of the card.
     */
    useEffect(() => {
        if (!tagContainerRef.current)
            return;
        const observer = ObserversService.newResizeObserver(adjustRenderedTags);
        ObserversService.observeResizeObserver(observer, tagContainerRef.current);
        adjustRenderedTags([tagContainerRef.current]);
        return () => ObserversService.disconnectResizeObserver(observer);
    }, [tagContainerRef])

    /**
     * With each change in the tagContainerRef and tags list of the state:
     * - determines the rendered tags of the component.
     */
    useEffect(() => {
        if (!tagContainerRef.current)
            return;
        adjustRenderedTags([tagContainerRef.current]);
    }, [tagContainerRef, tags])

    /**
     * Adjusts the number of tags to be rendered in the tagsContainer of the component.
     * @param {ResizeObserverEntry[] | Element[]} entries
     */
    const adjustRenderedTags = (entries) => {
        if (!entries?.length)
            return;

        const containerContentRect = entries[0]?.contentRect ?? entries[0].getBoundingClientRect();
        const width = containerContentRect?.width ?? 0;
        const height = containerContentRect?.height ?? 0;

        if (height !== containerDimensions.current.height &&
            width === containerDimensions.current.width) {
            return;
        }

        containerDimensions.current = {width: width, height: height};
        const tagElements = tagContainerRef.current.querySelectorAll('.tag');

        let tagsWidth = 0;
        let i = 0;
        const addedTagWidths = [];
        while (tagElements.length > i) {
            const tagElement = tagElements[i];
            let tagWidth = tagElement.getBoundingClientRect().width;

            const tagMR = parseInt(getComputedStyle(tagElement).marginRight?.replace('px', '') ?? '0') ?? 0
            tagsWidth += Number.isNaN(tagMR) ? 0 : tagMR;

            if (tagsWidth + tagWidth > width)
                break;

            addedTagWidths.push(tagWidth);
            tagsWidth += tagWidth;
            i++;
        }

        let countWidth = 0;
        if (tagElements.length - addedTagWidths.length) {
            /**@type {Element}*/
            const countElement = countRef.current.cloneNode(true);
            countElement.querySelector('p').innerText = `+${tagElements.length - addedTagWidths.length}`;
            countRef.current.appendChild(countElement);
            countWidth = countElement.getBoundingClientRect().width;
            countRef.current.removeChild(countElement);
        }

        while (tagsWidth + countWidth > width && addedTagWidths.length)
            tagsWidth -= addedTagWidths.pop();

        setCount(tagElements.length - addedTagWidths.length);
    }

    /**
     * Removes the icon of the given tag from the ui as its icon could not have been loaded.
     * @param {any} tag
     */
    const onIconErrored = (tag) => {
        setTags(prevState => prevState.map(e => e.id === tag.id
            ? {...e, iconErrored: true}
            : e
        ))
    }

    return (
        <>
            <div
                className={classnames('banner-preview-card', {selected})}
                onClick={() => toggleSelected(banner)}
            >
                <Banner
                    withShadow
                    url={banner?.imageFileInfo?.url}
                    initialHeight={200}
                    className={'my-2'}
                />
                <div className={'tags-container-parent'}>
                    <div ref={tagContainerRef} className={classnames('tags-container', {'no-tags': !tags?.length})}>
                        {
                            tags?.map((tag, i) => (
                                <div
                                    id={tag.id}
                                    key={tag.id}
                                    className={classnames('tag', {'hidden': i >= (tags.length - count) && i !== 0})}
                                >
                                    {
                                        !tag.iconErrored && !!tag?.iconInfo && (
                                            <div className={'tag-icon-container'}>
                                                {
                                                    tag?.iconInfo?.url?.toLowerCase()?.endsWith('.png')
                                                        ? <LazyImage
                                                            src={tag?.iconInfo?.url}
                                                            height={25}
                                                            width={25}
                                                            onError={() => onIconErrored(tag)}
                                                        />
                                                        : <LazySvg
                                                            src={tag?.iconInfo?.url}
                                                            height={25}
                                                            width={25}
                                                            onError={() => onIconErrored(tag)}
                                                        />
                                                }
                                            </div>
                                        )
                                    }
                                    <Tooltip arrow placement={'bottom'} title={tag?.name ?? ''}>
                                        <p>
                                            {tag.name ?? ''}
                                        </p>
                                    </Tooltip>
                                </div>
                            ))
                        }
                        <div className={classnames('count', {'d-none': !count})}>
                            <p>+{count}</p>
                        </div>
                    </div>
                </div>
                <div ref={countRef} className={classnames('count-ref')}><p/></div>
            </div>
        </>
    )
}

export default BannerSelectionDialog;
